import { API_BASE } from "../utils/api";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const TAX_YEARS = ["All Years", "2025", "2024", "2023", "2022", "2021"];

function formatFileSize(bytes) {
  if (!bytes) return "—";
  const k = 1024;
  const sizes = ["B", "KB", "MB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-ZA", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function FileIconBox({ fileType }) {
  const getStyle = () => {
    if (!fileType) return "bg-luxury-elevated text-luxury-muted";
    if (fileType.includes("pdf"))    return "bg-red-100     dark:bg-red-900/30    text-red-600    dark:text-red-400";
    if (fileType.includes("image"))  return "bg-purple-100  dark:bg-purple-900/30 text-purple-600 dark:text-purple-400";
    if (fileType.includes("word") || fileType.includes("document")) return "bg-luxury-elevated text-luxury-gold";
    if (fileType.includes("excel") || fileType.includes("sheet")) return "bg-emerald-100 dark:bg-green-900/30 text-emerald-600 dark:text-green-400";
    return "bg-luxury-elevated text-luxury-muted";
  };
  const getExt = () => {
    if (!fileType) return "FILE";
    if (fileType.includes("pdf")) return "PDF";
    if (fileType.includes("image")) return "IMG";
    if (fileType.includes("word") || fileType.includes("document")) return "DOC";
    if (fileType.includes("excel") || fileType.includes("sheet")) return "XLS";
    return "FILE";
  };
  return (
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${getStyle()}`}>
      <span className="text-xs font-bold tracking-tight">{getExt()}</span>
    </div>
  );
}

export default function TaxReturns() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState("All Years");
  const [search, setSearch] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchDocs();
    // eslint-disable-next-line
  }, []);

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/documents?category=tax_return&limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          setDocs(data);
        } else if (data && Array.isArray(data.documents)) {
          setDocs(data.documents);
        } else {
          setDocs([]);
        }
      } else {
        setDocs([]);
      }
    } catch (err) {
      setDocs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleting(true);
    try {
      await fetch(`${API_BASE}/api/documents/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocs((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {}
    setDeleting(false);
    setDeleteId(null);
  };

  const filtered = docs.filter((d) => {
    const yearMatch = selectedYear === "All Years" || d.tax_year === selectedYear;
    const searchMatch = !search || d.original_filename?.toLowerCase().includes(search.toLowerCase());
    return yearMatch && searchMatch;
  });

  const totalReturns = docs.length;
  const processedCount = docs.filter((d) => d.status === "processed").length;
  const pendingCount = docs.filter((d) => d.status === "pending").length;
  const years = [...new Set(docs.map((d) => d.tax_year).filter(Boolean))];

  return (
    <div className="min-h-screen bg-luxury-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">

        {/* Header Banner */}
        <div className="rounded-2xl border border-luxury-border bg-luxury-surface p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 bg-luxury-elevated border border-luxury-gold/30 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-luxury-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                  </svg>
                </div>
                <span className="text-luxury-muted text-sm font-medium">SARS Tax Portal</span>
              </div>
              <h1 className="text-2xl font-bold text-luxury-text">Tax Returns</h1>
              <p className="text-luxury-secondary text-sm mt-0.5">All your SARS tax return documents in one place</p>
            </div>
            <button
              onClick={() => navigate("/upload")}
              className="flex items-center gap-2 bg-luxury-cta hover:bg-luxury-cta-hover text-white px-4 py-2.5 rounded-xl font-semibold text-sm transition-all self-start sm:self-auto active:scale-95"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Upload Return
            </button>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            {
              label: "Total Returns",
              value: totalReturns,
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              ),
              iconStyle: "bg-luxury-elevated text-luxury-gold",
              valueStyle: "text-luxury-gold",
            },
            {
              label: "Processed",
              value: processedCount,
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
              iconStyle: "bg-luxury-cta/10 text-luxury-cta",
              valueStyle: "text-luxury-cta",
            },
            {
              label: "Pending",
              value: pendingCount,
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
              iconStyle: "bg-amber-100 dark:bg-yellow-900/30 text-amber-700 dark:text-yellow-400",
              valueStyle: "text-amber-700 dark:text-yellow-400",
            },
            {
              label: "Tax Years",
              value: years.length || 0,
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              ),
              iconStyle: "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400",
              valueStyle: "text-purple-700 dark:text-purple-400",
            },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl border border-luxury-border bg-luxury-surface p-4">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${s.iconStyle}`}>
                {s.icon}
              </div>
              <p className={`text-2xl font-bold ${s.valueStyle}`}>{loading ? "—" : s.value}</p>
              <p className="text-xs mt-0.5 text-luxury-secondary">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filter + Search Bar */}
        <div className="rounded-2xl border border-luxury-border bg-luxury-surface p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Tax Year Tabs */}
            <div className="flex gap-1.5 flex-wrap">
              {TAX_YEARS.map((yr) => (
                <button
                  key={yr}
                  onClick={() => setSelectedYear(yr)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    selectedYear === yr
                      ? "bg-luxury-cta text-white"
                      : "text-luxury-secondary hover:text-luxury-text hover:bg-luxury-elevated"
                  }`}
                >
                  {yr}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative sm:ml-auto">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-luxury-muted"
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search returns..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 rounded-xl border border-luxury-border text-sm outline-none focus:ring-2 focus:ring-luxury-gold/30 w-full sm:w-56 bg-luxury-elevated text-luxury-text placeholder-luxury-muted transition-all"
              />
            </div>
          </div>
        </div>

        {/* Documents Table */}
        <div className="rounded-2xl border border-luxury-border overflow-hidden bg-luxury-surface">

          {/* Table Header */}
          <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-luxury-border text-xs font-semibold uppercase tracking-wider bg-luxury-elevated text-luxury-muted">
            <div className="col-span-5">Document</div>
            <div className="col-span-2 text-center">Tax Year</div>
            <div className="col-span-2 text-center">Status</div>
            <div className="col-span-2 text-right">Date</div>
            <div className="col-span-1"></div>
          </div>

          {/* Rows */}
          {loading ? (
            <div className="px-5 py-16 text-center">
              <div className="w-8 h-8 border-2 border-luxury-gold border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm text-luxury-secondary">Loading tax returns...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="m-4 rounded-xl border-2 border-dashed border-luxury-border py-16 text-center">
              <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center bg-luxury-elevated">
                <svg className="w-7 h-7 text-luxury-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                </svg>
              </div>
              <p className="font-semibold text-sm text-luxury-text">No tax returns found</p>
              <p className="text-xs mt-1 mb-4 text-luxury-secondary">
                {search || selectedYear !== "All Years"
                  ? "Try adjusting your filters"
                  : "Upload your first tax return document"}
              </p>
              <button
                onClick={() => navigate("/upload")}
                className="px-4 py-2 bg-luxury-cta hover:bg-luxury-cta-hover text-white text-xs font-semibold rounded-xl transition-all"
              >
                Upload Tax Return
              </button>
            </div>
          ) : (
            filtered.map((doc, idx) => (
              <div
                key={doc.id}
                className={`grid grid-cols-12 gap-2 items-center px-5 py-3.5 transition-colors group hover:bg-luxury-elevated
                  ${idx !== filtered.length - 1 ? "border-b border-luxury-border" : ""}`}
              >
                {/* Document name */}
                <div className="col-span-5 flex items-center gap-3 min-w-0">
                  <FileIconBox fileType={doc.file_type} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate text-luxury-text">
                      {doc.original_filename || doc.filename}
                    </p>
                    <p className="text-xs truncate text-luxury-secondary">
                      {formatFileSize(doc.file_size)}
                    </p>
                  </div>
                </div>

                {/* Tax Year */}
                <div className="col-span-2 text-center">
                  {doc.tax_year ? (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-luxury-elevated border border-luxury-gold/30 text-luxury-gold">
                      {doc.tax_year}
                    </span>
                  ) : (
                    <span className="text-xs text-luxury-muted">—</span>
                  )}
                </div>

                {/* Status */}
                <div className="col-span-2 text-center">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold capitalize
                    ${doc.status === "processed"
                      ? "bg-luxury-cta/10 text-luxury-cta border border-luxury-cta/20"
                      : doc.status === "pending"
                      ? "bg-amber-100 dark:bg-yellow-900/30 text-amber-700 dark:text-yellow-400 border border-amber-200 dark:border-yellow-400/20"
                      : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-400/20"
                    }`}>
                    <span className={`w-1.5 h-1.5 rounded-full
                      ${doc.status === "processed" ? "bg-luxury-cta"
                        : doc.status === "pending" ? "bg-amber-500 dark:bg-yellow-400"
                        : "bg-red-500"}`} />
                    {doc.status || "pending"}
                  </span>
                </div>

                {/* Date */}
                <div className="col-span-2 text-right text-xs text-luxury-secondary">
                  {formatDate(doc.created_at)}
                </div>

                {/* Delete */}
                <div className="col-span-1 flex justify-end">
                  <button
                    onClick={() => setDeleteId(doc.id)}
                    className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg transition-all text-luxury-muted hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))
          )}

          {/* Footer count */}
          {!loading && filtered.length > 0 && (
            <div className="px-5 py-3 border-t border-luxury-border text-xs text-luxury-muted">
              Showing {filtered.length} of {docs.length} tax returns
            </div>
          )}
        </div>

        {/* SARS Info Card */}
        <div className="rounded-2xl border border-luxury-border bg-luxury-surface p-5">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-luxury-elevated text-luxury-gold">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-luxury-text">SARS Filing Reminder</p>
              <p className="text-xs mt-1 leading-relaxed text-luxury-secondary">
                South African tax year runs from 1 March to 28/29 February. SARS e-Filing season
                typically opens in July. Keep all your ITR12, IRP5, and supporting documents uploaded
                and organised here for easy access during filing season.
              </p>
            </div>
          </div>
        </div>

      </div>

      {/* Delete Confirm Modal */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl border border-luxury-border bg-luxury-surface p-6 shadow-2xl">
            <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <h3 className="text-base font-bold text-center mb-1 text-luxury-text">Delete Tax Return?</h3>
            <p className="text-sm text-center mb-5 text-luxury-secondary">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteId(null)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold border border-luxury-border text-luxury-secondary hover:bg-luxury-elevated transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteId)}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-all disabled:opacity-60"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
